import { useEffect, useMemo, useState } from "react";

import { mergePdfs } from "@libs/pdf";

export const useMergedPdfUrl = (pdfUrls: string[]) => {
  const [mergedUrl, setMergedUrl] = useState<string | null>(null);

  const urlsKey = useMemo(() => pdfUrls.join("|"), [pdfUrls]);

  useEffect(() => {
    if (pdfUrls.length === 0) {
      setMergedUrl(null);
      return;
    }

    let isRevoked = false;

    const merge = async () => {
      const pdfByteArrays = await Promise.all(
        pdfUrls.map(async url => {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(
              `Failed to fetch PDF (${response.status}) from ${url}`,
            );
          }
          return new Uint8Array(await response.arrayBuffer());
        }),
      );

      const mergedBytes = await mergePdfs(pdfByteArrays);
      const blob = new Blob([mergedBytes.buffer as ArrayBuffer], {
        type: "application/pdf",
      });
      const objectUrl = URL.createObjectURL(blob);

      if (!isRevoked) {
        setMergedUrl(objectUrl);
      } else {
        URL.revokeObjectURL(objectUrl);
      }
    };

    void merge();

    return () => {
      isRevoked = true;
      setMergedUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [pdfUrls, urlsKey]);

  return mergedUrl;
};
